<template>
  <div class="flex h-13">
    <div class="sticky z-10 flex pl-6 bg-slate-100 left-88">
      <div
        class="flex items-center border-b border-l border-r border-slate-400 bg-pure-white"
        :class="{ 'rounded-bl': isLast }"
      >
        <div class="flex flex-shrink-0 w-20 gap-2 px-4">
          <BlTextBody2
            :text="appointment.id"
            class="truncate text-slate-1000"
          />
        </div>
        <div class="flex items-center flex-shrink-0 gap-2 px-4 w-60">
          <BlIcon
            v-if="isOrphan"
            icon="warning"
            color="tomato-7"
            class="w-4 h-4"
          />
          <BlTextBody2
            v-if="appointment.client && appointment.client.name"
            :title="appointment.client.name"
            :text="appointment.client.name"
            class="truncate text-slate-1000"
          />
          <BlInputButton
            v-if="isOrphan"
            type="outlined"
            label="Reschedule"
            dense
            @clicked="rescheduleOpened = true"
            class="ml-auto"
          />
        </div>
      </div>
    </div>
    <div
      class="flex items-center flex-shrink-0 w-40 px-4 border-b bg-pure-white border-slate-400"
    >
      <BlChip v-if="isOrphan" chip="No therapist assigned" color="tomato" dense />
      <BlChip v-else :chip="appointment.status" dense />
    </div>
    <div
      class="flex items-center flex-shrink-0 w-40 pl-4 border-b bg-pure-white border-slate-400"
    >
      <BlTextBody2 :text="displayDate(appointment.date)" />
    </div>
    <div
      class="flex items-center flex-shrink-0 w-40 px-4 border-b bg-pure-white border-slate-400"
    >
      <BlTextBody2
        :text="
          `${appointment.start_time_string} - ${appointment.end_time_string}`
        "
      />
    </div>
    <div
      v-if="enableBranch"
      class="flex items-center flex-shrink-0 px-4 border-b bg-pure-white w-50 border-slate-400"
    >
      <BlTextBody2 v-if="!appointment.room.branch" text="-" />
      <div
        v-else
        class="flex items-center justify-center px-2 truncate rounded h-7"
        :style="{
          backgroundColor: appointment.room.branch.background_color || 'unset'
        }"
        :title="appointment.room.branch.name"
      >
        <BlTextBody2
          class="truncate"
          :text="appointment.room.branch.name || '-'"
          :style="{
            color: appointment.room.branch.color || 'unset'
          }"
        />
      </div>
    </div>
    <div
      class="flex items-center flex-shrink-0 px-4 border-b bg-pure-white w-50 border-slate-400"
      :title="appointment.room.name"
    >
      <BlTextBody2 :text="appointment.room.name" class="truncate" />
    </div>
    <div
      class="flex items-center flex-shrink-0 px-4 border-b bg-pure-white w-50 border-slate-400"
    >
      <BlTextBody2 v-if="!appointment.user" text="-" class="truncate" />
      <BlTextBody2
        v-else
        :title="appointment.user.name || 'Deleted user'"
        :text="appointment.user.name || 'Deleted user'"
        class="truncate"
        :class="{ 'italic ': !appointment.user.name }"
      />
    </div>
    <div
      class="flex items-center flex-shrink-0 px-4 border-b bg-pure-white w-80 border-slate-400"
    >
      <BlTextBody2
        v-if="appointment.supervisors.length"
        :text="appointment.supervisors.map(i => i.name).join(', ')"
        :title="appointment.supervisors.map(i => i.name).join(', ')"
        class="truncate"
      />
      <BlTextBody2 v-else text="-" />
    </div>
    <div
      class="flex items-center flex-grow flex-shrink-0 px-4 border-b bg-pure-white w-80 border-slate-400"
      :title="reasonText"
    >
      <BlTextBody2
        :text="reasonText"
        class="truncate whitespace-pre-line capitalize-first"
      />
    </div>
    <div class="sticky right-0 z-10 flex pr-6 bg-slate-100">
      <div
        class="flex items-center justify-center flex-shrink-0 gap-2 border-b border-r bg-pure-white w-14 border-slate-400"
        :class="{ 'rounded-br': isLast }"
      >
        <div
          v-if="
            appointment.status !== 'booking_in_progress' &&
              appointment.status !== 'booking_in_review'
          "
          class="relative z-10"
        >
          <BlInputButton
            @clicked="menuOpened = !menuOpened"
            icon="dots-vertical"
            type="plain"
            class="flex items-center justify-center w-6 h-6 hover:bg-hover-state"
          />
          <div
            v-if="menuOpened"
            class="absolute top-0 z-50 flex flex-col gap-1 p-2 mr-4 border rounded right-full bg-pure-white border-slate-400"
          >
            <router-link
              :to="`/appointments/details/${appointment.id}`"
              target="_blank"
              class="px-4 py-2 text-left transition-all duration-300 rounded outline-none hover:bg-hover-state min-w-56"
            >
              <BlTextBody2
                class="text-slate-1000"
                text="View appointment details"
              />
            </router-link>
            <button
              v-if="currentUser.role.includes('admin')"
              @click="rescheduleOpened = true"
              class="px-4 py-2 text-left transition-all duration-300 rounded outline-none min-w-56"
              :class="[
                appointment.status !== 'scheduled'
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-hover-state'
              ]"
              :disabled="appointment.status !== 'scheduled'"
            >
              <BlTextBody2
                class="text-slate-1000"
                text="Reschedule appointment"
              />
            </button>
            <button
              v-if="currentUser.role.includes('admin')"
              @click="cancelOpened = true"
              class="px-4 py-2 text-left transition-all duration-300 rounded outline-none min-w-56"
              :class="[
                appointment.status === 'cancelled' ||
                appointment.status === 'completed'
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-hover-state'
              ]"
              :disabled="
                appointment.status === 'cancelled' ||
                  appointment.status === 'completed'
              "
            >
              <BlTextBody2 class="text-error-400" text="Cancel appointment" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <RescheduleAppointmentOverlay
      v-if="rescheduleOpened && currentUser.role.includes('admin')"
      :appointmentId="appointment.id"
      @close-modal="rescheduleOpened = false"
      @after-commit="$emit('after-commit')"
    />
    <CancelAppointmentModal
      v-if="cancelOpened && currentUser.role.includes('admin')"
      :appointmentId="appointment.id"
      @close-modal="cancelOpened = false"
      @after-commit="$emit('after-commit')"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

import RescheduleAppointmentOverlay from "../overlay/RescheduleAppointmentOverlay.vue";
import CancelAppointmentModal from "../modal/CancelAppointmentModal.vue";

import BlTextBody2 from "../../BlTextBody2.vue";
import BlChip from "../../BlChip.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "AppointmentItem",
  components: {
    RescheduleAppointmentOverlay,
    CancelAppointmentModal,
    //
    BlTextBody2,
    BlChip,
    BlIcon,
    BlInputButton
  },
  props: {
    appointment: { type: Object, required: true },
    isLast: { required: false }
    // @after-commit
  },
  data() {
    return {
      menuOpened: false,
      rescheduleOpened: false,
      cancelOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "centerReasonSettings"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    isOrphan() {
      return !this.appointment.user && this.appointment.status === "scheduled";
    },
    reasonText() {
      if (!this.appointment) return "-";

      /**
       * cancellation_reason -- other_cancellation_reason
       * reschedule_reason -- other_reschedule_reason
       */

      const cancelReason = this.appointment.cancellation_reason;
      const rescheduleReason = this.appointment.reschedule_reason;

      const cancelCenterReason = this.centerReasonSettings.find(
        i => i.formatted_name === cancelReason
      );
      const rescheduleCenterReason = this.centerReasonSettings.find(
        i => i.formatted_name === rescheduleReason
      );

      if (cancelCenterReason) {
        if (cancelCenterReason.is_default) {
          return (
            this.appointment.other_cancellation_reason ||
            cancelCenterReason.name
          );
        } else return cancelCenterReason.name;
      }
      if (cancelReason) return cancelReason.replaceAll("_", " ");

      if (rescheduleCenterReason) {
        if (rescheduleCenterReason.is_default) {
          return (
            this.appointment.other_reschedule_reason ||
            rescheduleCenterReason.name
          );
        } else return rescheduleCenterReason.name;
      }
      if (rescheduleReason) return rescheduleReason.replaceAll("_", " ");

      return "-";
    }
  },
  watch: {
    menuOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandler);
        }, 10);
      }
    }
  },
  methods: {
    ...mapActions([]),
    displayDate(date) {
      return moment(date).format("DD MMMM YYYY");
    },
    closeHandler() {
      window.removeEventListener("click", this.closeHandler);
      this.menuOpened = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/table/Item.vue